import React from 'react';
import CloumsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import SortBy from '@intelligence/components/TableComponents/components/SortBy';
import TableShowData from '@intelligence/components/TableComponents/components/TableShowData';
import {
  getActualWidthOfChars,
  valueFormat,
} from '@intelligence/utils/commonUtils';
import { formatRobloxMetricRatioGrowth } from '@intelligence/v3/utils/robloxGrowthFormat';
import {
  getRobloxMetricRatioKey,
  ROBLOX_TOP_CHARTS_SERVER_SIZE_COLUMN,
} from '../../config/robloxTopChartsMetrics';
import {
  getRobloxTopChartsSortListKey,
  ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS,
} from '../../config/robloxTopChartsSort';
import { formatRobloxSupportFeatureText } from '../../utils/formatRobloxSupportFeature';
import {
  formatRobloxTableGenreLabel,
  type TRobloxGenreLabelMap,
} from '@intelligence/v3/utils/robloxGenreLabel';

import type { SortByItemType, SortByListItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { IntlShape } from 'react-intl';
import type { IRobloxTopChartsRow } from '../../types';
import type { IRobloxTopChartsSortableColumn } from '../../config/robloxTopChartsSort';

const PLACEHOLDER = '-';
const METRIC_COLUMN_MIN_WIDTH = 180;
const METRIC_COLUMN_PADDING = 40;
const METRIC_COLUMN_SORT_ICON_WIDTH = 28;
const RATIO_DISPLAY_MAX = '99999+%';
const TEXT_COLUMN_MIN_WIDTH = 160;
const TEXT_COLUMN_PADDING = 40;
const RELEASE_DATE_COLUMN_WIDTH = 140;
const ROBLOX_GENRE_COLUMN_WIDTH = 220;
const ROBLOX_TABLE_SHOW_DATA_CELL_CLASS = 'dbi-roblox-table-show-data-cell';
const ROBLOX_TABLE_HEADER_CLASS = 'dbi-roblox-table-metric-header';

const renderRightAlignedHeader = (
  columnTitle: string,
  columnWidth: number,
  hoverTipsKey?: string,
) => (
  <CloumsCenter width={columnWidth} align="right" hoverTipsKey={hoverTipsKey}>
    <SortBy
      className={ROBLOX_TABLE_HEADER_CLASS}
      title={columnTitle}
      defaultKey=""
      defaultValue=""
      align="right"
      isNoDropdown
      titlePointerCursor={Boolean(hoverTipsKey)}
    />
  </CloumsCenter>
);

const renderLeftAlignedHeader = (
  columnTitle: string,
  columnWidth: number,
  hoverTipsKey?: string,
) => (
  <CloumsCenter width={columnWidth} align="left" hoverTipsKey={hoverTipsKey}>
    <SortBy
      className={ROBLOX_TABLE_HEADER_CLASS}
      title={columnTitle}
      defaultKey=""
      defaultValue=""
      align="left"
      isNoDropdown
      titlePointerCursor={Boolean(hoverTipsKey)}
    />
  </CloumsCenter>
);

interface IRobloxTopChartsSortColumnOptions {
  currentSortKey: string;
  currentSortValue: 'asc' | 'desc';
  onSortChange: (payload: SortByItemType) => void;
}

const formatTableDate = (value?: string): string => {
  if (!value) {
    return PLACEHOLDER;
  }

  return value;
};

const formatMetricCellValue = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return PLACEHOLDER;
  }

  return String(valueFormat(value));
};

const renderMetricWithRatio = (
  value?: number | null,
  ratioValue?: number | null,
) => {
  const growthDataObj = formatRobloxMetricRatioGrowth(ratioValue);

  return (
    <TableShowData
      maxNumber={RATIO_DISPLAY_MAX}
      value={formatMetricCellValue(value)}
      type={growthDataObj?.type}
      growth={growthDataObj?.value}
    />
  );
};

const renderMetricCell = (
  columnWidth: number,
  row: IRobloxTopChartsRow,
  metric: string,
) => (
  <CloumsCenter
    width={columnWidth}
    align="right"
    classNameValue={ROBLOX_TABLE_SHOW_DATA_CELL_CLASS}
  >
    {renderMetricWithRatio(row?.[metric], row?.[getRobloxMetricRatioKey(metric)])}
  </CloumsCenter>
);

const formatTextCellValue = (value?: string | null): string => {
  if (value === null || value === undefined) {
    return PLACEHOLDER;
  }

  const text = String(value).trim();
  return text || PLACEHOLDER;
};

const RobloxGenreCell: React.FC<{
  mainGenre?: string;
  subGenre?: string;
  formatMessage: IntlShape['formatMessage'];
}> = ({ mainGenre, subGenre, formatMessage }) => {
  const main = mainGenre || PLACEHOLDER;
  const sub = subGenre || PLACEHOLDER;

  return (
    <div className="general_table_genre">
      <p className="main_genre_general">
        <span>
          {formatMessage({ id: 'dbi_genre_main' })} : {main}
        </span>
      </p>
      <p className="sub_genre_general">
        <span>
          {formatMessage({ id: 'dbi_genre_sub' })} : {sub}
        </span>
      </p>
    </div>
  );
};

const getSortableColumnWidth = (columnTitle: string, minWidth: number): number => {
  const titleWidth = getActualWidthOfChars(columnTitle, {
    fontSize: 14,
    fontWeight: 700,
  });

  return Math.max(
    minWidth,
    Math.ceil(titleWidth)
      + METRIC_COLUMN_PADDING
      + METRIC_COLUMN_SORT_ICON_WIDTH,
  );
};

const getColumnWidth = (columnTitle: string, minWidth: number): number => {
  const titleWidth = getActualWidthOfChars(columnTitle, {
    fontSize: 14,
    fontWeight: 700,
  });

  return Math.max(
    minWidth,
    Math.ceil(titleWidth) + (minWidth === METRIC_COLUMN_MIN_WIDTH ? METRIC_COLUMN_PADDING : TEXT_COLUMN_PADDING),
  );
};

const buildMetricSortList = (
  column: IRobloxTopChartsSortableColumn,
  columnTitle: string,
  formatMessage: IntlShape['formatMessage'],
): SortByListItemType[] => {
  const changeLabel = formatMessage({ id: 'dbi_change' });

  return [
    {
      key: getRobloxTopChartsSortListKey(column.columnKey, 0),
      sortMetric: column.metric,
      text: columnTitle,
      title: columnTitle,
      ratioType: 0,
    },
    {
      key: getRobloxTopChartsSortListKey(column.columnKey, 1),
      sortMetric: column.metric,
      text: `${columnTitle}:${changeLabel}(%)`,
      title: `${columnTitle}(%)`,
      ratioType: 1,
    },
  ];
};

const renderMetricValueOnlyCell = (
  columnWidth: number,
  row: IRobloxTopChartsRow,
  metric: string,
) => (
  <CloumsCenter width={columnWidth} align="right">
    {formatMetricCellValue(row?.[metric])}
  </CloumsCenter>
);

const createNonSortableMetricColumn = (
  column: {
    columnKey: string;
    metric: string;
    labelKey: string;
    hoverTipsKey?: string;
    valueOnly?: boolean;
  },
  formatMessage: IntlShape['formatMessage'],
) => {
  const columnTitle = formatMessage({ id: column.labelKey });
  const columnWidth = getColumnWidth(columnTitle, METRIC_COLUMN_MIN_WIDTH);

  return {
    key: column.columnKey,
    align: 'right' as const,
    width: columnWidth,
    header: (
      <CloumsCenter
        width={columnWidth}
        align="right"
        hoverTipsKey={column.hoverTipsKey}
      >
        <SortBy
          className={ROBLOX_TABLE_HEADER_CLASS}
          title={columnTitle}
          defaultKey=""
          defaultValue=""
          align="right"
          isNoDropdown
          titlePointerCursor={Boolean(column.hoverTipsKey)}
        />
      </CloumsCenter>
    ),
    render: (row: IRobloxTopChartsRow) =>
      column.valueOnly
        ? renderMetricValueOnlyCell(columnWidth, row, column.metric)
        : renderMetricCell(columnWidth, row, column.metric),
  };
};

const createSortableMetricColumn = (
  column: IRobloxTopChartsSortableColumn,
  formatMessage: IntlShape['formatMessage'],
  sortOptions: IRobloxTopChartsSortColumnOptions,
) => {
  const { currentSortKey, currentSortValue, onSortChange } = sortOptions;
  const columnTitle = formatMessage({ id: column.labelKey });
  const columnWidth = getSortableColumnWidth(columnTitle, METRIC_COLUMN_MIN_WIDTH);

  return {
    key: column.columnKey,
    align: 'right' as const,
    width: columnWidth,
    header: (
      <CloumsCenter
        width={columnWidth}
        align="right"
        hoverTipsKey={column.hoverTipsKey}
      >
        <SortBy
          className={ROBLOX_TABLE_HEADER_CLASS}
          title={columnTitle}
          defaultKey={currentSortKey}
          defaultValue={currentSortValue}
          align="right"
          list={buildMetricSortList(column, columnTitle, formatMessage)}
          onChange={onSortChange}
          titlePointerCursor={Boolean(column.hoverTipsKey)}
        />
      </CloumsCenter>
    ),
    render: (row: IRobloxTopChartsRow) =>
      renderMetricCell(columnWidth, row, column.metric),
  };
};

export const getRobloxTopChartsMetricColumns = (
  formatMessage: IntlShape['formatMessage'],
  sortOptions: IRobloxTopChartsSortColumnOptions,
) => {
  const sortableColumns = ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS.map((column) =>
    createSortableMetricColumn(column, formatMessage, sortOptions),
  );

  const serverSizeColumn = createNonSortableMetricColumn(
    ROBLOX_TOP_CHARTS_SERVER_SIZE_COLUMN,
    formatMessage,
  );

  return [...sortableColumns, serverSizeColumn];
};

interface IRobloxGenreColumnOptions {
  robloxGenreLabelMap?: TRobloxGenreLabelMap;
  isChinese?: boolean;
}

export const getRobloxTopChartsExtraColumns = (
  formatMessage: IntlShape['formatMessage'],
  genreColumnOptions?: IRobloxGenreColumnOptions,
) => {
  const robloxGenreLabelMap = genreColumnOptions?.robloxGenreLabelMap || new Map();
  const isChinese = genreColumnOptions?.isChinese ?? false;
  const formatGenreLabel = (genreKey?: string | null): string =>
    formatRobloxTableGenreLabel(genreKey, robloxGenreLabelMap, isChinese, PLACEHOLDER);

  const supportedFeatureTitle = formatMessage({ id: 'dbi_roblox_supported_features' });
  const supportedFeatureWidth = getColumnWidth(supportedFeatureTitle, TEXT_COLUMN_MIN_WIDTH);

  return [
    {
      key: 'support_feature',
      align: 'left' as const,
      width: supportedFeatureWidth,
      header: renderLeftAlignedHeader(
        supportedFeatureTitle,
        supportedFeatureWidth,
        'dbi_roblox_supported_features_tip',
      ),
      render: (row: IRobloxTopChartsRow) => (
        <CloumsCenter align="left" width={supportedFeatureWidth}>
          {formatTextCellValue(formatRobloxSupportFeatureText(row?.support_feature, formatMessage))}
        </CloumsCenter>
      ),
    },
    {
      key: 'release_time',
      align: 'left' as const,
      width: RELEASE_DATE_COLUMN_WIDTH,
      header: renderLeftAlignedHeader(
        formatMessage({ id: 'dbi_release_date' }),
        RELEASE_DATE_COLUMN_WIDTH,
      ),
      render: (row: IRobloxTopChartsRow) => (
        <CloumsCenter align="left" width={RELEASE_DATE_COLUMN_WIDTH}>
          {formatTableDate(row?.release_time)}
        </CloumsCenter>
      ),
    },
    {
      key: 'roblox_genre',
      align: 'left' as const,
      width: ROBLOX_GENRE_COLUMN_WIDTH,
      header: renderLeftAlignedHeader(
        formatMessage({ id: 'dbi_roblox_genre' }),
        ROBLOX_GENRE_COLUMN_WIDTH,
      ),
      render: (row: IRobloxTopChartsRow) => (
        <RobloxGenreCell
          mainGenre={formatGenreLabel(
            row?.roblox_sub_genre ?? row?.entity_info?.roblox_sub_genre,
          )}
          subGenre={formatGenreLabel(
            row?.roblox_level_3_genre ?? row?.entity_info?.roblox_level_3_genre,
          )}
          formatMessage={formatMessage}
        />
      ),
    },
  ];
};
